/**
 * Utilities for mapping schema questions to UI question format
 */

import type { LucideIcon } from 'lucide-react';
import {
  MapPin,
  AlertTriangle,
  FileText,
  ClipboardList,
  Droplets,
  Shield,
  Settings,
  CheckSquare,
} from 'lucide-react';
import type {
  InspectionSchemaDetailResponse,
  SchemaStageQuestions,
  SchemaQuestion,
  ResponseType,
  Severity,
} from '../types/inspectionSchema';

/**
 * UI Question types supported by the inspection form
 */
export type UIQuestionType =
  | 'yes-no'
  | 'yes-no-na'
  | 'select'
  | 'text'
  | 'number'
  | 'photo';

/**
 * UI Question format used by LiteInspectionFlowPage
 */
export interface UIQuestion {
  id: string;
  category: string;
  question: string;
  type: UIQuestionType;
  required: boolean;
  helpText?: string;
  options?: string[];
  // Extended fields from schema
  canonicalKey?: string;
  severity?: Severity | null;
  regulatoryReference?: string | null;
  requiresPhoto?: boolean;
  requiresComment?: boolean;
  conditionalOnQuestionId?: string | null;
  conditionalValue?: string | null;
  isHighPriority?: boolean;
}

/**
 * Inspection category with icon and questions
 */
export interface InspectionCategory {
  name: string;
  icon: LucideIcon;
  instructions?: string | null;
  questions: UIQuestion[];
}

/**
 * Map schema response_type to UI question type
 */
function mapResponseType(responseType: ResponseType): UIQuestionType {
  switch (responseType) {
    case 'yes_no':
      return 'yes-no';
    case 'yes_no_na':
      return 'yes-no-na';
    case 'rating':
      return 'select';
    case 'text':
      return 'text';
    default:
      return 'yes-no-na'; // Default to most common type
  }
}

/**
 * Get rating options for rating-type questions
 */
function getRatingOptions(): string[] {
  return ['1 - Poor', '2 - Fair', '3 - Good', '4 - Very Good', '5 - Excellent'];
}

/**
 * Infer an icon for a stage based on its name
 */
function inferIconForStage(stageName: string | null): LucideIcon {
  if (!stageName) return ClipboardList;

  const name = stageName.toLowerCase();

  if (
    name.includes('site') ||
    name.includes('perimeter') ||
    name.includes('entrance')
  ) {
    return MapPin;
  }
  if (
    name.includes('storm') ||
    name.includes('water') ||
    name.includes('drainage')
  ) {
    return Droplets;
  }
  if (
    name.includes('erosion') ||
    name.includes('sediment') ||
    name.includes('bmp')
  ) {
    return AlertTriangle;
  }
  if (
    name.includes('compliance') ||
    name.includes('permit') ||
    name.includes('document')
  ) {
    return FileText;
  }
  if (name.includes('safety') || name.includes('protection')) {
    return Shield;
  }
  if (name.includes('equipment') || name.includes('maintenance')) {
    return Settings;
  }
  if (name.includes('general') || name.includes('all')) {
    return CheckSquare;
  }

  return ClipboardList;
}

/**
 * Convert a single schema question to UI question format
 */
export function mapSchemaQuestionToUIQuestion(
  schemaQuestion: SchemaQuestion,
  categoryName: string
): UIQuestion {
  const baseQuestion: UIQuestion = {
    id: schemaQuestion.id,
    category: categoryName,
    question: schemaQuestion.question_text,
    type: mapResponseType(schemaQuestion.response_type),
    required: true, // Schema questions are generally required
    helpText: schemaQuestion.help_text || undefined,
    canonicalKey: schemaQuestion.canonical_key,
    severity: schemaQuestion.default_severity,
    regulatoryReference: schemaQuestion.regulatory_reference,
    requiresPhoto: schemaQuestion.requires_photo,
    requiresComment: schemaQuestion.requires_comment,
    conditionalOnQuestionId: schemaQuestion.conditional_on_question_id,
    conditionalValue: schemaQuestion.conditional_value,
    isHighPriority: schemaQuestion.is_high_priority || false,
  };

  // Add options for rating questions
  if (schemaQuestion.response_type === 'rating') {
    baseQuestion.options = getRatingOptions();
  }

  return baseQuestion;
}

/**
 * Generate a photo question to follow a question that requires photo documentation
 */
function createPhotoQuestion(
  parentQuestion: UIQuestion,
  categoryName: string
): UIQuestion {
  return {
    id: `${parentQuestion.id}_photo`,
    category: categoryName,
    question: `Take photo for: ${parentQuestion.question}`,
    type: 'photo',
    required: false, // Photo is supplementary
    helpText: 'Document this item with a photo',
    conditionalOnQuestionId: parentQuestion.id,
    conditionalValue: undefined, // Show after parent is answered
  };
}

/**
 * Transform a schema stage to an inspection category
 */
export function mapStageToCategory(
  stage: SchemaStageQuestions
): InspectionCategory {
  const categoryName = stage.construction_stage_name || 'General Inspection';
  const questions: UIQuestion[] = [];

  // Convert each schema question to UI format
  for (const schemaQuestion of stage.questions) {
    const uiQuestion = mapSchemaQuestionToUIQuestion(
      schemaQuestion,
      categoryName
    );
    questions.push(uiQuestion);

    // If the question requires a photo, add a photo question after it
    if (schemaQuestion.requires_photo) {
      questions.push(createPhotoQuestion(uiQuestion, categoryName));
    }
  }

  return {
    name: categoryName,
    icon: inferIconForStage(stage.construction_stage_name),
    instructions: stage.stage_instructions,
    questions,
  };
}

/**
 * Transform a full schema to inspection categories
 */
export function transformSchemaToCategories(
  schema: InspectionSchemaDetailResponse
): InspectionCategory[] {
  const stageCategories = [...schema.stages]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(mapStageToCategory)
    .filter((cat) => cat.questions.length > 0);

  // Schema-level questions (stage_questions_id == null) aren't covered by any
  // stage. Bundle them into a synthetic "General" category so a schema with
  // unattached questions still renders an inspection form.
  const orphanQuestions = (schema.questions || []).filter(
    (q) => !q.stage_questions_id
  );
  if (orphanQuestions.length === 0) return stageCategories;

  const generalQuestions: UIQuestion[] = [];
  for (const q of orphanQuestions) {
    const ui = mapSchemaQuestionToUIQuestion(q, 'General');
    generalQuestions.push(ui);
    if (q.requires_photo) {
      generalQuestions.push(createPhotoQuestion(ui, 'General'));
    }
  }

  return [
    {
      name: 'General',
      icon: inferIconForStage('General'),
      instructions: null,
      questions: generalQuestions,
    },
    ...stageCategories,
  ];
}

/**
 * Check if a question should be visible based on conditional logic
 */
export function isQuestionVisible(
  question: UIQuestion,
  responses: Record<string, { value: any }>
): boolean {
  // No conditional - always show
  if (!question.conditionalOnQuestionId) {
    return true;
  }

  const parentResponse = responses[question.conditionalOnQuestionId];

  // Parent not answered yet - hide conditional question
  if (!parentResponse) {
    return false;
  }

  // For photo questions (no specific conditional value), show after parent is answered
  if (question.type === 'photo' && !question.conditionalValue) {
    return true;
  }

  // Check if parent answer matches the conditional value
  const parentValue = parentResponse.value;

  // Handle boolean responses
  if (typeof parentValue === 'boolean') {
    if (
      question.conditionalValue === 'yes' ||
      question.conditionalValue === 'true'
    ) {
      return parentValue === true;
    }
    if (
      question.conditionalValue === 'no' ||
      question.conditionalValue === 'false'
    ) {
      return parentValue === false;
    }
  }

  // Handle string comparison
  return (
    String(parentValue).toLowerCase() ===
    String(question.conditionalValue).toLowerCase()
  );
}

/**
 * Get the total count of visible questions
 */
export function getVisibleQuestionCount(
  categories: InspectionCategory[],
  responses: Record<string, { value: any }>
): number {
  let count = 0;
  for (const category of categories) {
    for (const question of category.questions) {
      if (isQuestionVisible(question, responses)) {
        count++;
      }
    }
  }
  return count;
}

/**
 * Flatten categories into a single ordered list of visible questions
 */
export function getVisibleQuestions(
  categories: InspectionCategory[],
  responses: Record<string, { value: any }>
): Array<{
  question: UIQuestion;
  categoryIndex: number;
  questionIndex: number;
}> {
  const result: Array<{
    question: UIQuestion;
    categoryIndex: number;
    questionIndex: number;
  }> = [];

  categories.forEach((category, categoryIndex) => {
    category.questions.forEach((question, questionIndex) => {
      if (isQuestionVisible(question, responses)) {
        result.push({ question, categoryIndex, questionIndex });
      }
    });
  });

  return result;
}
